/**
 * Portfolio Builder — R1 + R2
 *
 * Slide-in drawer that takes a set of pinned strategy names + the already-fetched
 * cumulative + summary data and shows:
 *  1. Pinned strategies list with key metrics
 *  2. Correlation matrix (computed from daily returns in the equity curves)
 *  3. Optimizer toggle: Equal Weight / Risk Parity / Max Sharpe (approx)
 *  4. Combined equity curve at the chosen weights
 *  5. Total capital input → per-strategy allocation USD
 *  6. Deploy Basket → creates live Strategy instances + StrategyPortfolio (R2)
 */
import React, { useState, useMemo } from 'react';
import PlotlyChart from '../../../components/charts/PlotlyChart';
import { tradingService } from '../../../services/tradingService';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtPct  = v => v != null ? (v * 100).toFixed(1) + '%' : '—';
const fmtNum  = (v, d = 2) => v != null ? Number(v).toFixed(d) : '—';
const fmt$    = v => v != null ? '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—';
const pctCls  = v => v == null ? 'text-gray-400' : v >= 0 ? 'text-green-400' : 'text-red-400';

// Derive daily returns from cumulative series for one strategy column index
function dailyReturns(rows, ci) {
  const out = [];
  for (let t = 1; t < rows.length; t++) {
    const prev = rows[t - 1][ci];
    const curr = rows[t][ci];
    if (prev && curr && prev !== 0) out.push(curr / prev - 1);
    else out.push(null);
  }
  return out;
}

function stdDev(arr) {
  const clean = arr.filter(v => v != null);
  if (!clean.length) return 0;
  const mean = clean.reduce((s, v) => s + v, 0) / clean.length;
  return Math.sqrt(clean.reduce((s, v) => s + (v - mean) ** 2, 0) / clean.length) || 0.0001;
}

function pearson(a, b) {
  const pairs = a.map((v, i) => [v, b[i]]).filter(([x, y]) => x != null && y != null);
  if (pairs.length < 5) return null;
  const xa = pairs.map(p => p[0]);
  const xb = pairs.map(p => p[1]);
  const ma = xa.reduce((s, v) => s + v, 0) / xa.length;
  const mb = xb.reduce((s, v) => s + v, 0) / xb.length;
  const cov = xa.reduce((s, v, i) => s + (v - ma) * (xb[i] - mb), 0) / xa.length;
  const sa  = stdDev(xa);
  const sb  = stdDev(xb);
  return sa && sb ? Math.max(-1, Math.min(1, cov / (sa * sb))) : null;
}

// Compute weights for a given method
function computeWeights(method, basketNames, cumData, summaryData) {
  if (!basketNames.length) return {};

  if (method === 'equal') {
    const w = 1 / basketNames.length;
    return Object.fromEntries(basketNames.map(n => [n, w]));
  }

  const { columns, data: rows } = cumData;

  if (method === 'risk_parity') {
    // Inverse volatility weighting
    const vols = Object.fromEntries(basketNames.map(name => {
      const ci = columns.indexOf(name);
      if (ci < 0) return [name, 1];
      return [name, stdDev(dailyReturns(rows, ci))];
    }));
    const invVols = Object.fromEntries(Object.entries(vols).map(([n, v]) => [n, 1 / (v || 0.0001)]));
    const total   = Object.values(invVols).reduce((s, v) => s + v, 0);
    return Object.fromEntries(Object.entries(invVols).map(([n, v]) => [n, v / total]));
  }

  if (method === 'max_sharpe') {
    // Approximate: weight ∝ max(Sharpe, 0) — ignores correlations, R2 adds proper Markowitz
    const { index, columns: cols, data: srows } = summaryData.table;
    const si = cols.findIndex(c => c.toLowerCase().includes('sharpe'));
    const sharpes = Object.fromEntries(basketNames.map(name => {
      const ri = index.indexOf(name);
      const val = (ri >= 0 && si >= 0) ? (srows[ri][si] || 0) : 0;
      return [name, Math.max(0, val)];
    }));
    const total = Object.values(sharpes).reduce((s, v) => s + v, 0) || 1;
    return Object.fromEntries(Object.entries(sharpes).map(([n, v]) => [n, v / total]));
  }

  return Object.fromEntries(basketNames.map(n => [n, 1 / basketNames.length]));
}

// Combined equity curve (weighted average of normalized cumulative returns)
function combinedCurve(cumData, weights) {
  const { index: dates, columns, data: rows } = cumData;
  return dates.map((d, t) => {
    let combined = 0, totalW = 0;
    Object.entries(weights).forEach(([name, w]) => {
      const ci = columns.indexOf(name);
      if (ci < 0 || rows[t][ci] == null) return;
      combined += w * rows[t][ci];
      totalW   += w;
    });
    return { date: d, value: totalW > 0 ? combined / totalW : null };
  });
}

// ─── Correlation heatmap ──────────────────────────────────────────────────────
const CorrHeatmap = ({ names, cumData }) => {
  const { columns, data: rows } = cumData;

  const { z, text } = useMemo(() => {
    const series = Object.fromEntries(names.map(name => {
      const ci = columns.indexOf(name);
      return [name, ci >= 0 ? dailyReturns(rows, ci) : []];
    }));
    const z    = names.map(a => names.map(b => a === b ? 1 : pearson(series[a], series[b])));
    const text = z.map(row => row.map(v => v != null ? v.toFixed(2) : 'N/A'));
    return { z, text };
  }, [names, columns, rows]);

  // Short labels (max 15 chars)
  const labels = names.map(n => n.length > 15 ? n.slice(0, 13) + '…' : n);

  return (
    <PlotlyChart
      data={[{
        type: 'heatmap',
        z,
        x: labels,
        y: labels,
        text,
        texttemplate: '%{text}',
        colorscale: [
          [0, '#ef4444'], [0.5, '#1f2937'], [1, '#22c55e'],
        ],
        zmid: 0,
        zmin: -1,
        zmax: 1,
        showscale: true,
        colorbar: { thickness: 10, len: 0.8, title: { text: 'ρ', side: 'right' } },
      }]}
      layout={{
        margin: { t: 10, b: 60, l: 80, r: 40 },
        xaxis: { tickangle: -30, tickfont: { size: 9 } },
        yaxis: { tickfont: { size: 9 } },
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
        font: { color: '#9ca3af' },
      }}
      style={{ height: Math.max(200, names.length * 44 + 80) }}
    />
  );
};

// ─── Combined equity chart ────────────────────────────────────────────────────
const CombinedChart = ({ combined, cumData, weights }) => {
  const { index: dates, columns, data: rows } = cumData;

  const traces = useMemo(() => {
    const t = [];
    // Individual strategy lines (faint)
    Object.keys(weights).forEach((name, i) => {
      const ci = columns.indexOf(name);
      if (ci < 0) return;
      t.push({
        x: dates,
        y: rows.map(r => r[ci]),
        type: 'scatter',
        mode: 'lines',
        name,
        opacity: 0.35,
        line: { width: 1 },
      });
    });
    // Combined — bold gold
    t.push({
      x: combined.map(p => p.date),
      y: combined.map(p => p.value),
      type: 'scatter',
      mode: 'lines',
      name: 'Combined',
      line: { color: '#c9a96e', width: 2.5 },
    });
    return t;
  }, [combined, dates, columns, rows, weights]);

  return (
    <PlotlyChart
      data={traces}
      layout={{
        margin: { t: 10, b: 30, l: 50, r: 10 },
        xaxis: { showgrid: false },
        yaxis: { title: { text: 'Cumulative', font: { size: 9 } } },
        legend: { orientation: 'h', y: -0.15, font: { size: 9 } },
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
        font: { color: '#9ca3af' },
      }}
      style={{ height: 220 }}
    />
  );
};

// ─── Main drawer ──────────────────────────────────────────────────────────────
const METHODS = [
  { key: 'equal',       label: 'Equal Weight' },
  { key: 'risk_parity', label: 'Risk Parity' },
  { key: 'max_sharpe',  label: 'Max Sharpe ≈' },
];

const PortfolioBuilder = ({ basket, onRemove, onClear, onClose, cumData, summaryData }) => {
  const [method, setMethod]       = useState('equal');
  const [capital, setCapital]     = useState(50000);
  const [capitalInput, setCapitalInput] = useState('50000');

  // Deploy modal state
  const [deployOpen,   setDeployOpen]   = useState(false);
  const [deployName,   setDeployName]   = useState('');
  const [deploying,    setDeploying]    = useState(false);
  const [deployError,  setDeployError]  = useState('');
  const [deployedId,   setDeployedId]   = useState(null);   // portfolio id on success

  const basketNames = useMemo(() => [...basket], [basket]);

  // Strategy metadata from summary table
  const stratMeta = useMemo(() => {
    if (!summaryData?.table) return {};
    const { index, columns, data: rows } = summaryData.table;
    const sharpeIdx = columns.findIndex(c => c.toLowerCase().includes('sharpe'));
    const returnIdx = columns.findIndex(c => c.toLowerCase().includes('ann') && c.toLowerCase().includes('return'));
    const ddIdx     = columns.findIndex(c => c.toLowerCase().includes('drawdown'));
    const meta = {};
    index.forEach((name, ri) => {
      meta[name] = {
        sharpe: sharpeIdx >= 0 ? rows[ri][sharpeIdx] : null,
        annReturn: returnIdx >= 0 ? rows[ri][returnIdx] : null,
        maxDD: ddIdx >= 0 ? rows[ri][ddIdx] : null,
      };
    });
    return meta;
  }, [summaryData]);

  const weights = useMemo(() => {
    if (!cumData || !summaryData || !basketNames.length) return {};
    return computeWeights(method, basketNames, cumData, summaryData);
  }, [method, basketNames, cumData, summaryData]);

  const combined = useMemo(() => {
    if (!cumData || !Object.keys(weights).length) return [];
    return combinedCurve(cumData, weights);
  }, [cumData, weights]);

  // Allocation per strategy
  const allocations = useMemo(() =>
    Object.fromEntries(Object.entries(weights).map(([n, w]) => [n, Math.round(w * capital)])),
    [weights, capital]
  );

  const hasData = cumData && summaryData;

  const handleDeploy = async () => {
    if (!deployName.trim()) { setDeployError('Portfolio name is required'); return; }
    setDeploying(true);
    setDeployError('');
    try {
      // Build members list from basket + weights + metadata
      const meta = summaryData?.metadata || {};
      const members = basketNames.map(name => {
        const m = meta[name] || {};
        return {
          label:          name,
          weight:         weights[name] || 0,
          allocation_usd: allocations[name] || 0,
          strategy_class: m.strategy_class || '',
          symbol:         m.symbol || '',
          parameters:     m.parameters_dict || {},
        };
      });
      const res = await tradingService.deployBasket({
        name:             deployName.trim(),
        total_capital:    capital,
        optimizer_method: method,
        members,
      });
      setDeployedId(res.data.id);
      setDeployOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Deploy failed';
      setDeployError(msg);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl bg-white dark:bg-[#0d1117] border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div>
            <h2 className="font-serif text-lg text-gray-900 dark:text-gray-100">Portfolio Builder</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">{basketNames.length} strategies pinned</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClear} className="text-[11px] text-gray-400 hover:text-red-400 transition-colors">Clear all</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none ml-2">✕</button>
          </div>
        </div>

        {basketNames.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-500">Pin strategies from the Overview table to build a portfolio.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-6">

            {/* Pinned strategies */}
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Strategies</h3>
              <div className="space-y-1.5">
                {basketNames.map(name => {
                  const m = stratMeta[name] || {};
                  const w = weights[name];
                  return (
                    <div key={name} className="flex items-center justify-between bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{name}</div>
                        <div className="flex gap-3 text-[10px] mt-0.5">
                          <span className={pctCls(m.annReturn)}>Ret {fmtPct(m.annReturn)}</span>
                          <span className="text-gray-500">SR {fmtNum(m.sharpe)}</span>
                          <span className="text-red-400">DD {fmtPct(m.maxDD)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        {w != null && (
                          <div className="text-right">
                            <div className="text-sm font-semibold text-brand-gold tabular-nums">{(w * 100).toFixed(1)}%</div>
                            <div className="text-[10px] text-gray-500">{fmt$(allocations[name])}</div>
                          </div>
                        )}
                        <button
                          onClick={() => onRemove(name)}
                          className="text-gray-400 hover:text-red-400 transition-colors text-sm leading-none"
                          title="Remove from basket"
                        >✕</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Optimizer + Capital */}
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Optimizer</label>
                <div className="flex gap-1">
                  {METHODS.map(m => (
                    <button
                      key={m.key}
                      onClick={() => setMethod(m.key)}
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                        method === m.key
                          ? 'bg-brand-gold text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {method === 'max_sharpe' && (
                  <p className="text-[10px] text-gray-500 mt-1">≈ Proportional to individual Sharpe (full Markowitz in R2)</p>
                )}
              </div>

              <div className="ml-auto">
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Total Capital</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    min="1000"
                    step="5000"
                    value={capitalInput}
                    onChange={e => {
                      setCapitalInput(e.target.value);
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v > 0) setCapital(v);
                    }}
                    className="w-28 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-brand-gold"
                  />
                </div>
              </div>
            </div>

            {/* Correlation matrix */}
            {hasData && basketNames.length >= 2 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Correlation Matrix</h3>
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-3">
                  <CorrHeatmap names={basketNames} cumData={cumData} />
                </div>
              </div>
            )}

            {/* Combined equity curve */}
            {hasData && combined.length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Combined Equity Curve</h3>
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-3">
                  <CombinedChart combined={combined} cumData={cumData} weights={weights} />
                </div>
              </div>
            )}

            {/* Allocation summary */}
            {Object.keys(allocations).length > 0 && (
              <div>
                <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Allocation Summary</h3>
                <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                        <th className="text-left px-3 py-2">Strategy</th>
                        <th className="text-right px-3 py-2">Weight</th>
                        <th className="text-right px-3 py-2">Allocation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                      {Object.entries(allocations).map(([name, alloc]) => (
                        <tr key={name} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[240px]">{name}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-brand-gold font-medium">
                            {(weights[name] * 100).toFixed(1)}%
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                            {fmt$(alloc)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Footer — Deploy */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 shrink-0">
          {deployedId ? (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-3 text-center">
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">✓ Basket deployed!</p>
              <p className="text-xs text-gray-500 mt-0.5">Go to the <strong>Baskets</strong> tab to activate strategies.</p>
            </div>
          ) : (
            <>
              <button
                disabled={basketNames.length < 2}
                className="w-full py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-500 transition-colors"
                title={basketNames.length < 2 ? 'Pin at least 2 strategies' : 'Deploy this basket'}
                onClick={() => { setDeployOpen(true); setDeployName(''); setDeployError(''); }}
              >
                ⚡ Deploy Basket ({basketNames.length} strategies · {fmt$(capital)} total)
              </button>
              <p className="text-[10px] text-gray-500 text-center mt-1.5">
                Creates live Strategy instances for each, sized by the computed weights.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Deploy modal */}
      {deployOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => !deploying && setDeployOpen(false)}>
          <div className="w-full max-w-md bg-white dark:bg-[#161b22] rounded-2xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 mb-1">Name this basket</h3>
            <p className="text-xs text-gray-500 mb-4">
              {basketNames.length} strategies · {fmt$(capital)} total · {method.replace('_', ' ')} weights
            </p>

            <input
              type="text"
              value={deployName}
              onChange={e => setDeployName(e.target.value)}
              placeholder="e.g. Momentum Basket Q2"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-brand-gold mb-1"
              onKeyDown={e => e.key === 'Enter' && !deploying && handleDeploy()}
              autoFocus
            />

            {deployError && (
              <p className="text-xs text-red-400 mb-3">{deployError}</p>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setDeployOpen(false)}
                disabled={deploying}
                className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeploy}
                disabled={deploying || !deployName.trim()}
                className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-500 disabled:opacity-40 transition-colors"
              >
                {deploying ? 'Deploying…' : '⚡ Deploy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioBuilder;
